import { I18n } from "@/i18n/i18n";

interface Season {
    name: "halloween" | "christmas" | "winter";
    // Inclusive local dates as [month, day], month 0-indexed
    from: [number, number];
    to: [number, number];
    // Emoji for the success confetti, or the falling-snow effect
    confetti: string[] | "snow";
    // mdi icon names for the success message
    icons: string[];
}

const DEFAULT_ICONS = [
    "check-bold",
    "thumb-up-outline",
    "emoticon-happy-outline",
    "emoticon-excited-outline",
    "emoticon-cool-outline",
    "shimmer",
    "party-popper",
    "arm-flex-outline",
    "emoticon-kiss-outline",
    "robot-outline",
    "cow",
    "unicorn-variant",
];
const WINTER_ICONS = ["snowflake", "pine-tree", "gift", "cookie", "snowman"];

// The first season that matches wins, so the narrow ones go before "winter"
const SEASONS: Season[] = [
    {
        name: "halloween",
        from: [9, 28],
        to: [10, 3],
        confetti: ["👻", "🎃", "🦇"],
        icons: ["ghost", "halloween", "spider", "skull"],
    },
    { name: "christmas", from: [11, 22], to: [11, 28], confetti: ["🎄", "🎁", "⭐"], icons: WINTER_ICONS },
    { name: "winter", from: [11, 1], to: [11, 31], confetti: "snow", icons: WINTER_ICONS },
];

// Seasonal effects are for users with Dutch language or Brussels timezone
function inSeasonRegion(): boolean {
    const isNl = I18n.locale() === "nl";
    // eslint-disable-next-line new-cap
    const isBrusselsTime = Intl.DateTimeFormat().resolvedOptions().timeZone === "Europe/Brussels";
    return isNl || isBrusselsTime;
}

/** The season the given local date falls in, if the user is in the region. */
export function currentSeason(date: Date = new Date()): Season | undefined {
    if (!inSeasonRegion()) {
        return undefined;
    }
    const today = date.getMonth() * 100 + date.getDate();
    return SEASONS.find(({ from, to }) => today >= from[0] * 100 + from[1] && today <= to[0] * 100 + to[1]);
}

let confettiModule: Promise<typeof import("canvas-confetti") | undefined> | undefined;

/**
 * Starts loading canvas-confetti, which pages only need once something was handed in.
 * Resolves to undefined when the chunk fails to load, for example offline or after a deploy
 * removed it, and tries again on the next call.
 */
export function loadCelebration(): Promise<typeof import("canvas-confetti") | undefined> {
    confettiModule ??= import("canvas-confetti").catch(() => {
        confettiModule = undefined;
        return undefined;
    });
    return confettiModule;
}

const EMOJI_SCALAR = 2.4;
const EMOJI_TILTS = [-20, -10, 0, 10, 20];
// At most 1: confetti clips the shape to the bitmap's rect
const EMOJI_SIZES = [0.7, 0.85, 1];
type Shape = ReturnType<typeof import("canvas-confetti").shapeFromText>;
const emojiShapes = new Map<Season, Shape[]>();

// Flat confetti never rotates, so the tilt is drawn into the bitmap. Not into the shape's
// matrix: Safari paints the rest of the shape's rect around a rotated pattern opaque.
function tilted(shape: Shape, degrees: number): ImageBitmap {
    const { width, height } = shape.bitmap;
    const radians = (degrees * Math.PI) / 180;
    const cos = Math.abs(Math.cos(radians));
    const sin = Math.abs(Math.sin(radians));
    const canvas = new OffscreenCanvas(Math.ceil(width * cos + height * sin), Math.ceil(width * sin + height * cos));
    const context = canvas.getContext("2d")!;
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate(radians);
    context.drawImage(shape.bitmap, -width / 2, -height / 2);
    return canvas.transferToImageBitmap();
}

// Like shapeFromText: scales by 1 / EMOJI_SCALAR and centers the bitmap
function scaled(bitmap: ImageBitmap, size: number): Shape {
    const scale = size / EMOJI_SCALAR;
    return {
        type: "bitmap",
        bitmap,
        matrix: [scale, 0, 0, scale, (-bitmap.width * scale) / 2, (-bitmap.height * scale) / 2],
    };
}

type Confetti = (typeof import("canvas-confetti"))["default"];

function fireSnow(confetti: Confetti): void {
    const duration = 10_000;
    const rand = (min: number, max: number): number => Math.random() * (max - min) + min;
    const end = performance.now() + duration;
    let skew = 1;

    function frame(now: number): void {
        const timeLeft = end - now;
        if (timeLeft <= 0) {
            return;
        }

        const ticks = Math.max(200, 500 * (timeLeft / duration));
        skew = Math.max(0.8, skew - 0.001);

        void confetti({
            disableForReducedMotion: true,
            particleCount: 1,
            startVelocity: 0,
            ticks,
            origin: { x: Math.random(), y: Math.random() * skew - 0.2 },
            colors: ["#fff"],
            shapes: ["circle"],
            gravity: rand(0.4, 0.6),
            scalar: rand(0.4, 1),
            drift: rand(-0.4, 0.4),
        });

        requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
}

/** Fires confetti: emoji around Halloween and Christmas, snow in the rest of December. */
export async function celebrateSuccess(): Promise<void> {
    const module = await loadCelebration();
    if (!module) {
        return;
    }
    const confetti = module.default;

    const season = currentSeason();
    if (!season) {
        void confetti({
            disableForReducedMotion: true,
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
        });
        return;
    }

    if (season.confetti === "snow") {
        fireSnow(confetti);
        return;
    }

    const emoji = season.confetti;
    if (!emojiShapes.has(season)) {
        emojiShapes.set(
            season,
            emoji.flatMap((text) => {
                const shape = confetti.shapeFromText({ text, scalar: EMOJI_SCALAR });
                return EMOJI_TILTS.flatMap((degrees) => {
                    const bitmap = tilted(shape, degrees);
                    return EMOJI_SIZES.map((size) => scaled(bitmap, size));
                });
            }),
        );
    }
    void confetti({
        disableForReducedMotion: true,
        particleCount: 50,
        spread: 80,
        origin: { y: 0.6 },
        shapes: emojiShapes.get(season),
        scalar: EMOJI_SCALAR,
        flat: true,
    });
}

export function getPositiveEmoji(): string {
    const icons = currentSeason()?.icons ?? DEFAULT_ICONS;
    return "mdi-" + icons[Math.floor(Math.pow(Math.random(), 3) * icons.length)];
}
